import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Mic, Video, Camera, X, Play, Sparkles, Clock, MessageCircle, Heart, GraduationCap, Baby, Briefcase, Home, Check, AlertCircle, ChevronLeft } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import MediaCapture from '../components/MediaCapture';
import SharingScope from '../components/common/SharingScope';
import { createMilestone, getChapters } from '../services/chaptersAPI';
import { QUICK_PICK_TYPES } from '../services/chaptersAPI';
import { mediaAPI } from '../config/mediaAPI';
import { categoryConfig } from '../config/milestoneCategories';
import WhosePersonSelector from '../components/common/WhosePersonSelector';

// Map quick-pick types to milestone category configs
const QUICK_PICK_TO_CATEGORY = {
  graduation: 'education',
  wedding: 'family',
  baby: 'family',
  new_home: 'home',
  new_job: 'career',
  retirement: 'career',
  military: 'career',
  travel: 'travel',
  // Child types
  first_word: 'child_firsts',
  first_step: 'child_firsts',
  first_day_school: 'child_school',
  lost_tooth: 'child_firsts',
  learned_ride_bike: 'child_firsts',
  birthday: 'child_celebration',
  award: 'child_achievement',
  recital: 'child_achievement',
  sports: 'child_achievement',
  made_friend: 'child_social',
  funny_moment: 'child_personality',
};

// Child-specific quick-pick types
const CHILD_QUICK_PICK_TYPES = [
  { type: 'first_word', emoji: '🗣️', label: 'First Word' },
  { type: 'first_step', emoji: '👣', label: 'First Steps' },
  { type: 'first_day_school', emoji: '🎒', label: 'First Day of School' },
  { type: 'lost_tooth', emoji: '🦷', label: 'Lost a Tooth' },
  { type: 'learned_ride_bike', emoji: '🚲', label: 'Learned to Ride Bike' },
  { type: 'graduation', emoji: '🎓', label: 'Graduation' },
  { type: 'birthday', emoji: '🎂', label: 'Birthday' },
  { type: 'award', emoji: '🏆', label: 'Won an Award' },
  { type: 'recital', emoji: '🎭', label: 'Recital / Performance' },
  { type: 'sports', emoji: '⚽', label: 'Sports Achievement' },
  { type: 'made_friend', emoji: '🤝', label: 'Made a New Friend' },
  { type: 'funny_moment', emoji: '😂', label: 'Funny Moment' },
  { type: 'custom', emoji: '✨', label: 'Something Else' },
];

// Child-specific category configs for details
const childCategoryConfig = {
  child_firsts: {
    id: 'child_firsts',
    name: 'Firsts',
    emoji: '⭐',
    step1Title: 'What kind of first was this?',
    storyPlaceholder: 'Describe the moment... What happened? How did they react? How did you feel watching?',
    types: [
      { id: 'first-word', emoji: '🗣️', title: 'First Word', desc: 'What did they say?' },
      { id: 'first-step', emoji: '👣', title: 'First Steps', desc: 'Where were they? Who saw it?' },
      { id: 'first-tooth', emoji: '🦷', title: 'Lost First Tooth', desc: 'How did it happen?' },
      { id: 'first-bike', emoji: '🚲', title: 'Riding a Bike', desc: 'How long did it take to learn?' },
      { id: 'first-swim', emoji: '🏊', title: 'First Swim', desc: 'Were they scared or brave?' },
      { id: 'other', emoji: '✨', title: 'Another First', desc: 'You tell us!' },
    ],
    customFields: [
      { label: 'How old were they?', placeholder: 'e.g., 14 months, 2 years', key: 'age_at_milestone' },
      { label: 'Who was there?', placeholder: 'e.g., Mom, Dad, Grandma', key: 'witnesses' },
    ],
  },
  child_school: {
    id: 'child_school',
    name: 'School',
    emoji: '🎒',
    step1Title: 'What school milestone was this?',
    storyPlaceholder: 'Tell us about this school moment... How did they feel? Were they excited or nervous?',
    types: [
      { id: 'first-day', emoji: '🎒', title: 'First Day', desc: 'First day at a new school or grade' },
      { id: 'graduation', emoji: '🎓', title: 'Graduation', desc: 'Preschool, elementary, middle school' },
      { id: 'honor-roll', emoji: '📜', title: 'Honor Roll', desc: 'Academic achievement' },
      { id: 'field-trip', emoji: '🚌', title: 'Field Trip', desc: 'A memorable outing' },
      { id: 'other', emoji: '✨', title: 'Something Else', desc: 'You tell us!' },
    ],
    customFields: [
      { label: 'School Name', placeholder: 'e.g., Maple Elementary', key: 'school' },
      { label: 'Grade / Class', placeholder: 'e.g., Kindergarten, 3rd Grade', key: 'grade' },
    ],
  },
  child_celebration: {
    id: 'child_celebration',
    name: 'Celebration',
    emoji: '🎂',
    step1Title: 'What are we celebrating?',
    storyPlaceholder: 'What made this celebration special? Who was there? What was the highlight?',
    types: [
      { id: 'birthday', emoji: '🎂', title: 'Birthday', desc: 'Another year older!' },
      { id: 'holiday', emoji: '🎄', title: 'Holiday', desc: 'A holiday to remember' },
      { id: 'cultural', emoji: '🪔', title: 'Cultural Celebration', desc: 'Tradition or ceremony' },
      { id: 'other', emoji: '✨', title: 'Something Else', desc: 'You tell us!' },
    ],
    customFields: [
      { label: 'How old did they turn?', placeholder: 'e.g., 5', key: 'age' },
      { label: 'Theme or Venue', placeholder: 'e.g., Dinosaur party at the park', key: 'theme' },
    ],
  },
  child_achievement: {
    id: 'child_achievement',
    name: 'Achievement',
    emoji: '🏆',
    step1Title: 'What did they achieve?',
    storyPlaceholder: 'How hard did they work for this? What was the journey? How did they react?',
    types: [
      { id: 'award', emoji: '🏆', title: 'Won an Award', desc: 'Recognition for something great' },
      { id: 'recital', emoji: '🎭', title: 'Recital / Show', desc: 'Performance or presentation' },
      { id: 'sports', emoji: '⚽', title: 'Sports', desc: 'Athletic achievement or milestone' },
      { id: 'art', emoji: '🎨', title: 'Art / Creative', desc: 'Something they made or created' },
      { id: 'other', emoji: '✨', title: 'Something Else', desc: 'You tell us!' },
    ],
    customFields: [
      { label: 'Activity / Sport / Subject', placeholder: 'e.g., Soccer, Piano, Science Fair', key: 'activity' },
      { label: 'Award or Achievement Name', placeholder: 'e.g., MVP, Gold Star, 1st Place', key: 'award_name' },
    ],
  },
  child_social: {
    id: 'child_social',
    name: 'Social',
    emoji: '🤝',
    step1Title: 'What social milestone was this?',
    storyPlaceholder: 'Tell us about this moment... Who was involved? Why was it meaningful?',
    types: [
      { id: 'new-friend', emoji: '🤝', title: 'Made a Friend', desc: 'A new friendship started' },
      { id: 'playdate', emoji: '🏠', title: 'First Playdate', desc: 'Playing at someone\'s house' },
      { id: 'sleepover', emoji: '🛏️', title: 'First Sleepover', desc: 'Staying the night somewhere new' },
      { id: 'other', emoji: '✨', title: 'Something Else', desc: 'You tell us!' },
    ],
    customFields: [
      { label: 'Friend\'s Name', placeholder: 'e.g., Emma', key: 'friend_name' },
    ],
  },
  child_personality: {
    id: 'child_personality',
    name: 'Personality',
    emoji: '😂',
    step1Title: 'What made this moment special?',
    storyPlaceholder: 'What happened? Why is this so "them"? This is the kind of thing you\'ll want to remember forever.',
    types: [
      { id: 'funny', emoji: '😂', title: 'Funny Moment', desc: 'Something hilarious they said or did' },
      { id: 'kind', emoji: '💛', title: 'Act of Kindness', desc: 'They did something sweet' },
      { id: 'brave', emoji: '🦁', title: 'Brave Moment', desc: 'They conquered a fear' },
      { id: 'quirky', emoji: '🤪', title: 'Quirky Habit', desc: 'Something uniquely them' },
      { id: 'other', emoji: '✨', title: 'Something Else', desc: 'You tell us!' },
    ],
    customFields: [
      { label: 'What did they say or do?', placeholder: 'Quote them or describe the moment', key: 'quote', fullWidth: true },
    ],
  },
};

const SECTION_IDS = ['type', 'when', 'details', 'story', 'media'];

/**
 * AddChapterMilestone - Single-page layout to add a milestone
 *
 * All sections visible on one scrollable page:
 * 1. What happened? (quick pick type + title)
 * 2. When was it? (year + month, auto-assigns chapter)
 * 3. Details (category-specific fields, shown only when applicable)
 * 4. Tell the story (text)
 * 5. Media & sharing
 */
function AddChapterMilestone() {
  const { chapterId } = useParams();
  const navigate = useNavigate();

  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    location: '',
    quick_pick_type: 'custom',
    text: '',
    selectedChapterId: chapterId,
    details: {},
  });

  // Person selector state (whose milestone is this?)
  const [tier, setTier] = useState('mine');
  const [selectedChildIds, setSelectedChildIds] = useState([]);

  // Determine which quick-pick types and categories to use based on tier
  const isChildMilestone = tier === 'child';
  const quickPickTypes = isChildMilestone ? CHILD_QUICK_PICK_TYPES : QUICK_PICK_TYPES;

  // Get the category config for the selected quick-pick type
  const activeCategoryId = QUICK_PICK_TO_CATEGORY[formData.quick_pick_type];
  const activeCategory = activeCategoryId
    ? (childCategoryConfig[activeCategoryId] || categoryConfig[activeCategoryId])
    : null;

  const [sharingScope, setSharingScope] = useState('single_family');
  const [sharingFamilyIds, setSharingFamilyIds] = useState([]);

  // Media state
  const [isMediaCaptureOpen, setIsMediaCaptureOpen] = useState(false);
  const [localMediaFiles, setLocalMediaFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Pending Releases Modal State
  const [showPendingReleasesModal, setShowPendingReleasesModal] = useState(false);
  const [matchingReleases, setMatchingReleases] = useState([]);
  const [savedMilestoneData, setSavedMilestoneData] = useState(null);
  const [releasingItems, setReleasingItems] = useState(false);

  // Section refs for floating nav
  const sectionRefs = {
    type: useRef(null),
    when: useRef(null),
    details: useRef(null),
    story: useRef(null),
    media: useRef(null),
  };
  const [activeSection, setActiveSection] = useState('type');

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const offset = 120;
      for (const id of [...SECTION_IDS].reverse()) {
        const ref = sectionRefs[id];
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          if (rect.top <= offset) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    sectionRefs[id]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Mock pending releases data
  const pendingReleasesData = [
    {
      id: 1, eventType: 'marriage',
      keywords: ['married', 'wedding', 'spouse', 'wife', 'husband'],
      quickPickTypes: ['marriage'],
      label: 'When you get married', icon: Heart, color: '#E65B8C',
      items: [
        { type: 'capsule', from: 'Mom', title: 'For your wedding day' },
        { type: 'capsule', from: 'Dad', title: 'Marriage advice from Dad' },
        { type: 'story', from: 'Grandma', title: 'How I met your grandfather' },
      ],
    },
    {
      id: 2, eventType: 'graduation',
      keywords: ['graduated', 'graduation', 'degree', 'diploma', 'university', 'college'],
      quickPickTypes: ['graduation'],
      label: 'When you graduate', icon: GraduationCap, color: '#4A6FA5',
      items: [{ type: 'capsule', from: 'Dad', title: 'Proud of you' }],
    },
    {
      id: 3, eventType: 'first_child',
      keywords: ['baby', 'born', 'child', 'son', 'daughter', 'parent', 'birth'],
      quickPickTypes: ['first_child'],
      label: 'When you have a child', icon: Baby, color: '#6B8E23',
      items: [
        { type: 'capsule', from: 'Mom', title: 'Welcome to parenthood' },
        { type: 'capsule', from: 'Dad', title: 'Being a parent' },
        { type: 'capsule', from: 'Grandma', title: 'Family traditions' },
      ],
    },
    {
      id: 4, eventType: 'new_home',
      keywords: ['house', 'home', 'moved', 'moving', 'apartment', 'bought'],
      quickPickTypes: ['new_home', 'moved'],
      label: 'When you move to a new home', icon: Home, color: '#C67A4A',
      items: [{ type: 'story', from: 'Mom', title: 'Our first house memories' }],
    },
    {
      id: 5, eventType: 'new_job',
      keywords: ['job', 'career', 'work', 'promotion', 'hired', 'started working'],
      quickPickTypes: ['career', 'job'],
      label: 'When you start a new job', icon: Briefcase, color: '#7B6BA4',
      items: [{ type: 'capsule', from: 'Uncle John', title: 'Career advice' }],
    },
  ];

  const findMatchingReleases = (milestoneData) => {
    const titleLower = milestoneData.title.toLowerCase();
    const quickPickType = milestoneData.quick_pick_type;
    return pendingReleasesData.filter(release => {
      if (release.quickPickTypes.includes(quickPickType)) return true;
      return release.keywords.some(keyword => titleLower.includes(keyword));
    });
  };

  // Load chapters data
  useEffect(() => {
    async function loadData() {
      try {
        const chaptersData = await getChapters();
        setChapters(chaptersData);
        if (chapterId) {
          const chapter = chaptersData.find(c => c.id === chapterId);
          setCurrentChapter(chapter);
          if (chapter) {
            setFormData(prev => ({
              ...prev,
              year: chapter.start_year + 5,
              selectedChapterId: chapterId,
            }));
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Could not load chapters');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [chapterId]);

  // Auto-assign chapter based on selected year
  useEffect(() => {
    if (chapters.length > 0 && formData.year) {
      const matchingChapter = findChapterForYear(formData.year, chapters);
      if (matchingChapter) {
        setCurrentChapter(matchingChapter);
        setFormData(prev => ({ ...prev, selectedChapterId: matchingChapter.id }));
      }
    }
  }, [formData.year, chapters]);

  const findChapterForYear = (year, chaptersList) => {
    const currentYear = new Date().getFullYear();
    const sorted = [...chaptersList].sort((a, b) => b.start_year - a.start_year);
    for (const chapter of sorted) {
      const endYear = chapter.end_year || currentYear;
      if (year >= chapter.start_year && year <= endYear) return chapter;
    }
    return chaptersList.reduce((closest, chapter) => {
      const end = chapter.end_year || currentYear;
      const dist = Math.min(Math.abs(year - chapter.start_year), Math.abs(year - end));
      const closestDist = closest
        ? Math.min(Math.abs(year - closest.start_year), Math.abs(year - (closest.end_year || currentYear)))
        : Infinity;
      return dist < closestDist ? chapter : closest;
    }, null);
  };

  const handleQuickPick = (type) => {
    const quickPick = quickPickTypes.find(q => q.type === type);
    setFormData(prev => ({
      ...prev,
      quick_pick_type: type,
      title: quickPick?.label || prev.title,
      details: {},
    }));
  };

  const handleYearChange = (delta) => {
    setFormData(prev => ({
      ...prev,
      year: Math.max(1900, Math.min(new Date().getFullYear(), prev.year + delta)),
    }));
  };

  const handleMonthSelect = (month) => {
    setFormData(prev => ({
      ...prev,
      month: prev.month === month ? null : month,
    }));
  };

  // Media handling
  const handleMediaCaptured = (mediaData) => {
    const localFile = {
      id: `local-${Date.now()}-${Math.random()}`,
      file: mediaData.file,
      preview: mediaData.preview,
      name: mediaData.file.name,
      size: mediaData.file.size,
      type: mediaData.file.type,
      mediaType: mediaData.type,
      duration: mediaData.duration,
    };
    setLocalMediaFiles(prev => [...prev, localFile]);
  };

  const handleFileUpload = (file, mediaType) => {
    const previewUrl = URL.createObjectURL(file);
    const localFile = {
      id: `local-${Date.now()}-${Math.random()}`,
      file, preview: previewUrl,
      name: file.name, size: file.size, type: file.type,
      mediaType,
    };
    setLocalMediaFiles(prev => [...prev, localFile]);
  };

  const createFileInput = (accept, maxSize, mediaType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = true;
    input.onchange = (e) => {
      Array.from(e.target.files).forEach(file => {
        if (file.size > maxSize) {
          const maxMB = Math.round(maxSize / (1024 * 1024));
          const fileMB = (file.size / (1024 * 1024)).toFixed(mediaType === 'image' ? 1 : 0);
          const label = mediaType === 'video' ? 'Videos' : mediaType === 'audio' ? 'Audio files' : 'Images';
          alert(`"${file.name}" is too large (${fileMB} MB). ${label} must be under ${maxMB} MB.`);
          return;
        }
        handleFileUpload(file, mediaType);
      });
    };
    input.click();
  };

  const handleImageUpload = () => createFileInput('image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif', 10 * 1024 * 1024, 'image');
  const handleVideoUpload = () => createFileInput('video/mp4,video/avi,video/quicktime,video/wmv,video/webm,.mov', 500 * 1024 * 1024, 'video');
  const handleAudioUpload = () => createFileInput('audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg', 50 * 1024 * 1024, 'audio');

  const removeLocalMedia = (fileId) => {
    setLocalMediaFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) URL.revokeObjectURL(fileToRemove.preview);
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    Array.from(e.dataTransfer.files).forEach(file => {
      const mt = file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'image';
      const maxMB = mt === 'video' ? 500 : mt === 'audio' ? 50 : 10;
      if (file.size > maxMB * 1024 * 1024) {
        alert(`"${file.name}" is too large (${(file.size/(1024*1024)).toFixed(0)} MB). ${mt === 'video' ? 'Videos' : mt === 'audio' ? 'Audio files' : 'Images'} must be under ${maxMB} MB.`);
        return;
      }
      handleFileUpload(file, mt);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (includeStory = true) => {
    if (tier === 'child' && selectedChildIds.length === 0) {
      setError('Please select at least one child');
      return;
    }
    if (!formData.title.trim()) {
      setError('Please enter a title');
      scrollToSection('type');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const photoIds = [];
      let voiceFileId = null;
      let voiceDuration = null;

      for (const media of localMediaFiles) {
        try {
          let mediaType = 'image';
          if (media.mediaType === 'video') mediaType = 'video';
          else if (media.mediaType === 'audio') mediaType = 'audio';

          const uploadResult = await mediaAPI.uploadMedia({
            file: media.file,
            media_type: mediaType,
            title: media.name || formData.title,
          });

          if (media.mediaType === 'audio') {
            voiceFileId = uploadResult.id;
            voiceDuration = media.duration || null;
          } else {
            photoIds.push(uploadResult.id);
          }
        } catch (uploadErr) {
          console.error('Error uploading media file:', uploadErr);
        }
      }

      const detailsPayload = {};
      if (formData.details && Object.keys(formData.details).length > 0) {
        Object.entries(formData.details).forEach(([key, value]) => {
          if (value) detailsPayload[key] = value;
        });
      }

      await createMilestone({
        chapter: formData.selectedChapterId,
        title: formData.title,
        year: formData.year,
        month: formData.month,
        location: formData.location,
        quick_pick_type: formData.quick_pick_type,
        text: includeStory ? formData.text : '',
        details: Object.keys(detailsPayload).length > 0 ? detailsPayload : undefined,
        photo_ids: photoIds.length > 0 ? photoIds : undefined,
        voice_file_id: voiceFileId || undefined,
        voice_duration: voiceDuration || undefined,
        sharing_scope: sharingScope,
        family_ids: sharingFamilyIds,
        person_id: tier === 'child' && selectedChildIds.length > 0 ? selectedChildIds[0] : undefined,
      });

      const matches = findMatchingReleases(formData);
      if (matches.length > 0) {
        setSavedMilestoneData(formData);
        setMatchingReleases(matches);
        setShowPendingReleasesModal(true);
      } else {
        navigate('/my-life');
      }
    } catch (err) {
      console.error('Error creating milestone:', err);
      setError('Could not save milestone. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (chapterId) {
      navigate('/my-life');
    } else {
      navigate('/my-life');
    }
  };

  const months = [
    { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
  ];

  const sectionNav = [
    { id: 'type', label: 'Type' },
    { id: 'when', label: 'When' },
    { id: 'details', label: 'Details' },
    { id: 'story', label: 'Story' },
    { id: 'media', label: 'Media' },
  ];

  if (loading) {
    return (
      <HubLayout>
        <div className="min-h-screen bg-fb-cream flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive"></div>
        </div>
      </HubLayout>
    );
  }

  return (
    <HubLayout>
      <div className="min-h-screen bg-fb-cream">
        {/* Fixed Top Header */}
        <div className="sticky top-0 bg-fb-olive text-white z-40 shadow-md">
          <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="text-white/80 hover:text-white transition-colors"
              >
                <ChevronLeft size={24} />
              </button>
              <h1 className="font-serif text-lg font-bold">Add a Milestone</h1>
            </div>
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={saving}
              className="px-5 py-2 bg-[#E65100] text-white rounded-lg font-semibold text-sm hover:opacity-90 transition-all shadow-lg disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Milestone'}
            </button>
          </div>
        </div>

        {/* Floating Section Nav (right side) */}
        <div className="fixed right-4 top-1/2 -translate-y-1/2 z-30 hidden md:flex flex-col items-end gap-3">
          {sectionNav.filter(s => s.id !== 'details' || activeCategory).map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="group flex items-center gap-2"
            >
              <span className="text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-2 py-1 rounded shadow">
                {s.label}
              </span>
              <div className={`w-3 h-3 rounded-full border-2 border-white shadow transition-all ${
                activeSection === s.id ? 'bg-fb-olive scale-125' : 'bg-gray-300'
              }`} />
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="max-w-2xl mx-auto px-6 pt-8 pb-32">
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* ============================== */}
          {/* SECTION 0: Whose milestone?    */}
          {/* ============================== */}
          <div id="section-whose" className="scroll-mt-24 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-serif text-xl font-bold text-gray-900">Whose milestone is this?</h2>
            </div>
            <WhosePersonSelector
              tier={tier}
              onTierChange={(newTier) => {
                setTier(newTier);
                // Reset quick-pick type when switching between adult and child
                setFormData(prev => ({ ...prev, quick_pick_type: 'custom', title: '', details: {} }));
              }}
              selectedChildIds={selectedChildIds}
              onSelectedChildIdsChange={setSelectedChildIds}
              itemLabel="milestone"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-300 my-2" />

          {/* ============================== */}
          {/* SECTION 1: What Happened?      */}
          {/* ============================== */}
          <div ref={sectionRefs.type} id="section-type" className="scroll-mt-24 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-fb-olive text-white flex items-center justify-center text-sm font-bold">1</div>
              <h2 className="font-serif text-xl font-bold text-gray-900">
                {isChildMilestone ? "What milestone is this?" : "What happened?"}
              </h2>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              {/* Quick Pick Grid */}
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                {isChildMilestone ? "Pick a child milestone" : "Pick a type"}
              </label>
              <div className={`grid gap-2 mb-6 ${isChildMilestone ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-3 sm:grid-cols-5'}`}>
                {quickPickTypes.map((qp) => (
                  <button
                    key={qp.type}
                    type="button"
                    onClick={() => handleQuickPick(qp.type)}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      formData.quick_pick_type === qp.type
                        ? 'border-fb-olive bg-fb-olive/10'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl block">{qp.emoji}</span>
                    <span className="text-xs font-medium text-gray-700">{qp.label}</span>
                  </button>
                ))}
              </div>

              {/* Title */}
              <label className="block font-semibold text-gray-800 mb-2">Give it a title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Married Martha"
                className="w-full px-4 py-4 text-xl border-2 border-gray-200 rounded-lg focus:border-fb-olive focus:outline-none"
              />
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-300 my-2" />

          {/* ============================== */}
          {/* SECTION 2: When Was It?        */}
          {/* ============================== */}
          <div ref={sectionRefs.when} id="section-when" className="scroll-mt-24 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-fb-olive text-white flex items-center justify-center text-sm font-bold">2</div>
              <h2 className="font-serif text-xl font-bold text-gray-900">When was it?</h2>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row gap-6">
                {/* Year Picker */}
                <div className="flex-shrink-0">
                  <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Year</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleYearChange(-1)}
                      className="w-10 h-10 rounded-lg bg-fb-olive text-white flex items-center justify-center text-lg font-bold hover:bg-fb-olive/90 transition-colors"
                    >
                      &larr;
                    </button>
                    <input
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      value={formData.year}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setFormData(prev => ({ ...prev, year: '' }));
                        } else {
                          const num = parseInt(val, 10);
                          if (!isNaN(num)) {
                            setFormData(prev => ({ ...prev, year: Math.min(new Date().getFullYear(), Math.max(0, num)) }));
                          }
                        }
                      }}
                      onBlur={() => {
                        if (!formData.year || formData.year < 1900) {
                          setFormData(prev => ({ ...prev, year: 1900 }));
                        }
                      }}
                      className="w-28 h-12 border-2 border-gray-300 rounded-lg text-center text-2xl font-serif font-bold text-gray-900 focus:border-fb-olive focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleYearChange(1)}
                      className="w-10 h-10 rounded-lg bg-fb-olive text-white flex items-center justify-center text-lg font-bold hover:bg-fb-olive/90 transition-colors"
                    >
                      &rarr;
                    </button>
                  </div>
                </div>

                {/* Month Picker */}
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 mb-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Month</label>
                    <span className="text-xs text-gray-400">(optional)</span>
                  </div>
                  <div className="grid grid-cols-6 gap-1.5">
                    {months.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => handleMonthSelect(m.value)}
                        className={`py-2 px-1 rounded-lg border-2 text-xs font-medium transition-all ${
                          formData.month === m.value
                            ? 'border-fb-olive bg-fb-olive text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Auto-assigned Chapter */}
              {currentChapter && (
                <div className="mt-5 bg-fb-warm rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📖</span>
                    <div>
                      <div className="text-xs text-gray-500">Auto-assigned chapter</div>
                      <div className="font-serif font-bold text-gray-900">
                        Chapter {currentChapter.number}: "{currentChapter.title}"
                      </div>
                      <div className="text-xs text-gray-500">
                        {currentChapter.start_year} – {currentChapter.end_year || 'Present'}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {/* TODO: Open chapter picker modal */}}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-600 hover:bg-white transition-colors"
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-300 my-2" />

          {/* ============================== */}
          {/* SECTION 3: Details (contextual)*/}
          {/* ============================== */}
          <div ref={sectionRefs.details} id="section-details" className="scroll-mt-24 mb-8">
            {activeCategory ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-fb-olive text-white flex items-center justify-center text-sm font-bold">3</div>
                  <h2 className="font-serif text-xl font-bold text-gray-900">
                    {activeCategory.emoji} {activeCategory.name} Details
                  </h2>
                </div>
                <p className="text-sm text-gray-500 ml-10 mb-4">These help tell the full story. All optional.</p>

                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
                  {/* Subtype chips */}
                  {activeCategory.types && activeCategory.types.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">What type?</label>
                      <div className="flex flex-wrap gap-2">
                        {activeCategory.types.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              details: { ...prev.details, subtype: prev.details.subtype === t.id ? null : t.id }
                            }))}
                            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 text-sm font-medium transition-all ${
                              formData.details.subtype === t.id
                                ? 'border-fb-olive bg-fb-olive/10'
                                : 'border-gray-200 text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <span>{t.emoji}</span> {t.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Custom Fields */}
                  {activeCategory.customFields && activeCategory.customFields.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {activeCategory.customFields.map((field) => (
                        <div key={field.key} className={field.fullWidth ? 'sm:col-span-2' : ''}>
                          <label className="block font-semibold text-gray-800 mb-2 text-sm">{field.label}</label>
                          <input
                            type="text"
                            value={formData.details[field.key] || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              details: { ...prev.details, [field.key]: e.target.value }
                            }))}
                            placeholder={field.placeholder}
                            className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-lg focus:border-fb-olive focus:outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Location */}
                  <div>
                    <label className="block font-semibold text-gray-800 mb-2 text-sm">Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., Chicago, Illinois"
                      className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-lg focus:border-fb-olive focus:outline-none"
                    />
                  </div>
                </div>
              </>
            ) : (
              /* No category — just show location field */
              <>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-fb-olive text-white flex items-center justify-center text-sm font-bold">3</div>
                  <h2 className="font-serif text-xl font-bold text-gray-900">Details</h2>
                </div>
                <p className="text-sm text-gray-500 ml-10 mb-4">Optional details about this milestone.</p>

                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                  <label className="block font-semibold text-gray-800 mb-2 text-sm">Where did this happen?</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., Chicago, Illinois"
                    className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-lg focus:border-fb-olive focus:outline-none"
                  />
                </div>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-300 my-2" />

          {/* ============================== */}
          {/* SECTION 4: Tell the Story      */}
          {/* ============================== */}
          <div ref={sectionRefs.story} id="section-story" className="scroll-mt-24 mb-8">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-fb-olive text-white flex items-center justify-center text-sm font-bold">4</div>
              <h2 className="font-serif text-xl font-bold text-gray-900">Tell the story</h2>
            </div>
            <p className="text-sm text-gray-500 ml-10 mb-4">Optional — your family will love to read it!</p>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <textarea
                value={formData.text}
                onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                placeholder={activeCategory?.storyPlaceholder || 'What happened? How did it feel? Who was there with you?'}
                rows={5}
                disabled={saving}
                className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:border-fb-olive focus:bg-white transition-all resize-y disabled:opacity-50"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">You can always add more later</span>
                {formData.text.length > 0 && (
                  <span className="text-xs text-gray-400">{formData.text.length} characters</span>
                )}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-300 my-2" />

          {/* ============================== */}
          {/* SECTION 5: Media & Sharing     */}
          {/* ============================== */}
          <div ref={sectionRefs.media} id="section-media" className="scroll-mt-24 mb-8">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-fb-olive text-white flex items-center justify-center text-sm font-bold">5</div>
              <h2 className="font-serif text-xl font-bold text-gray-900">Add media</h2>
            </div>
            <p className="text-sm text-gray-500 ml-10 mb-4">Photos, videos, or voice recordings</p>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              {/* Media buttons row */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                <button
                  type="button"
                  onClick={() => setIsMediaCaptureOpen(true)}
                  className="p-3 rounded-xl border-2 border-gray-200 hover:border-green-500 hover:shadow-md bg-white transition-all group text-center"
                >
                  <div className="flex flex-col items-center">
                    <div className="relative mb-1">
                      <Video className="w-7 h-7 text-green-600 group-hover:text-green-700" />
                      <Mic className="w-4 h-4 text-green-500 absolute -bottom-1 -right-1" />
                    </div>
                    <div className="font-semibold text-xs text-gray-800">Record</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleImageUpload}
                  className="p-3 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:shadow-md bg-white transition-all group text-center"
                >
                  <div className="flex flex-col items-center">
                    <Camera className="w-7 h-7 text-blue-600 group-hover:text-blue-700 mb-1" />
                    <div className="font-semibold text-xs text-gray-800">Photos</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleVideoUpload}
                  className="p-3 rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:shadow-md bg-white transition-all group text-center"
                >
                  <div className="flex flex-col items-center">
                    <Video className="w-7 h-7 text-purple-600 group-hover:text-purple-700 mb-1" />
                    <div className="font-semibold text-xs text-gray-800">Videos</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleAudioUpload}
                  className="p-3 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:shadow-md bg-white transition-all group text-center"
                >
                  <div className="flex flex-col items-center">
                    <Mic className="w-7 h-7 text-orange-600 group-hover:text-orange-700 mb-1" />
                    <div className="font-semibold text-xs text-gray-800">Audio</div>
                  </div>
                </button>
              </div>

              {/* Drop zone */}
              <div
                className={`p-4 border-2 border-dashed rounded-xl transition-all text-center ${
                  isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <p className={`text-sm ${isDragOver ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                  {isDragOver ? 'Drop photos here!' : 'Drag & drop photos here, or use buttons above'}
                </p>
              </div>

              {/* Media Files Preview */}
              {localMediaFiles.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Attached ({localMediaFiles.length})
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    {localMediaFiles.map((media) => (
                      <div key={media.id} className="relative group">
                        {media.mediaType === 'image' ? (
                          <img
                            src={media.preview}
                            alt={media.name}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          />
                        ) : media.mediaType === 'video' ? (
                          <div className="w-20 h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative">
                            <video src={media.preview} className="w-full h-full object-cover rounded-lg" muted />
                            <Play className="w-6 h-6 text-white absolute drop-shadow-lg" />
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                            <Mic className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1 truncate max-w-[80px]">{media.name}</div>
                        <div className="text-xs text-gray-400">{formatFileSize(media.size)}</div>
                        <button
                          type="button"
                          onClick={() => removeLocalMedia(media.id)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow text-xs"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sharing Scope */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm mt-4">
              <SharingScope
                value={sharingScope}
                onChange={(scope, familyIds) => {
                  setSharingScope(scope);
                  setSharingFamilyIds(familyIds);
                }}
              />
            </div>
          </div>
        </div>

        {/* Sticky Bottom Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              &larr; Cancel
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleSubmit(false)}
                disabled={saving}
                className="px-5 py-3 text-gray-500 text-sm underline hover:text-gray-700 disabled:opacity-50 hidden sm:inline-block"
              >
                Save without story
              </button>
              <button
                type="button"
                onClick={() => handleSubmit(true)}
                disabled={saving}
                className="px-8 py-3 bg-[#E65100] text-white rounded-xl font-semibold hover:opacity-90 transition-all shadow-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Milestone \u2713'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MediaCapture Modal */}
      <MediaCapture
        isOpen={isMediaCaptureOpen}
        onClose={() => setIsMediaCaptureOpen(false)}
        onMediaCaptured={handleMediaCaptured}
      />

      {/* Pending Releases Modal */}
      {showPendingReleasesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8" />
                <h2 className="text-2xl font-bold">Special Moment Detected!</h2>
              </div>
              <p className="text-white/90">
                Your family has prepared messages waiting for this milestone.
              </p>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <div className="text-sm text-gray-500 mb-1">You just added:</div>
                <div className="font-semibold text-lg text-gray-900">
                  "{savedMilestoneData?.title}"
                </div>
              </div>

              <div className="space-y-4">
                {matchingReleases.map((release) => {
                  const ReleaseIcon = release.icon;
                  return (
                    <div key={release.id} className="border-2 border-gray-200 rounded-xl overflow-hidden">
                      <div
                        className="flex items-center gap-3 p-4"
                        style={{ backgroundColor: `${release.color}10` }}
                      >
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${release.color}20`, color: release.color }}
                        >
                          <ReleaseIcon size={24} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{release.label}</div>
                          <div className="text-sm text-gray-600">
                            {release.items.length} item{release.items.length > 1 ? 's' : ''} ready to be released
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 border-t border-gray-200">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          What's waiting:
                        </div>
                        <div className="space-y-2">
                          {release.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-sm">
                              <div className="flex items-center gap-1 text-gray-400">
                                {item.type === 'capsule' ? <Clock size={14} /> : <MessageCircle size={14} />}
                              </div>
                              <span className="text-gray-700 italic">"{item.title}"</span>
                              <span className="text-gray-500">from {item.from}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-start gap-3 mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>Important:</strong> Once released, these items will be delivered immediately and cannot be undone.
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPendingReleasesModal(false);
                    navigate('/my-life');
                  }}
                  className="flex-1 py-3 px-4 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Not yet
                </button>
                <button
                  onClick={() => {
                    setReleasingItems(true);
                    setTimeout(() => {
                      setReleasingItems(false);
                      setShowPendingReleasesModal(false);
                      navigate('/my-life');
                    }, 1500);
                  }}
                  disabled={releasingItems}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {releasingItems ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Releasing...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Release them now!
                    </>
                  )}
                </button>
              </div>
              <p className="text-center text-sm text-gray-500 mt-3">
                You can also release items later from Settings &rarr; Pending Releases
              </p>
            </div>
          </div>
        </div>
      )}
    </HubLayout>
  );
}

export default AddChapterMilestone;
